// src/components/InternalActionItemListView.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useOutletContext, useNavigate, useParams } from 'react-router-dom';
import ActionItemCard from './ActionItemCard';
import EisenhowerMatrixView from './EisenhowerMatrixView';
import CreateEntityModal from './CreateEntityModal';
import { ACTION_ITEM_STATUS_OPTIONS } from '../constants';
import { ROUTES } from '../constants/routes';
import { DepartmentSelector, usePersistedFilters, DropdownCheckbox, Tooltip, SingleSelectDropdown } from './FilterControls';
import SmartFilterBar from './SmartFilterBar';
import { RiInformationLine, RiAddLine } from 'react-icons/ri';
import { useEntityNavigationContext } from './EntityNavigation';
import { useStaffContext } from '../contexts/StaffContext';

// LocalStorage key for Eisenhower Matrix visibility (shared across views)
const EISENHOWER_VISIBLE_KEY = 'cortex-eisenhower-visible';

const InternalActionItemListView = () => {
    const { apiService, departmentId: departmentIdFromContext, dateRange, setDateRange } = useOutletContext();
    const navigate = useNavigate();
    const params = useParams();
    const { getDriDisplayName } = useStaffContext();
    
    // Get filter expansion state from EntityNavigation context
    const { filtersExpanded } = useEntityNavigationContext();
    
    // Filters are always collapsible (controlled by EntityNavigation header)
    const filterVariant = 'collapsible';
    
    const activeRouteDepartmentId = departmentIdFromContext || params.departmentId;
    
    const [departments, setDepartments] = useState([]);
    const [selectedDepartment, setSelectedDepartment] = useState(activeRouteDepartmentId || '');
    const [loadingDepartments, setLoadingDepartments] = useState(true);
    
    const [actionItemsList, setActionItemsList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    
    
    const [actionItemAssigneeFilter, setActionItemAssigneeFilter] = useState('');
    const [assigneeList, setAssigneeList] = useState([]);
    const [loadingAssignees, setLoadingAssignees] = useState(false);

    const [actionItemInitiativeFilter, setActionItemInitiativeFilter] = useState('');
    const [initiativeListForFilter, setInitiativeListForFilter] = useState([]);
    const [loadingInitiativesForFilter, setLoadingInitiativesForFilter] = useState(false);
    
    const [dris, setDris] = useState([]);
    const [selectedDris, setSelectedDris] = usePersistedFilters('selectedDris', []);
    
    // Create modal state
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [staffList, setStaffList] = useState([]);
    const [allClients, setAllClients] = useState([]);
    
    // Related Clients filter
    const [relatedClients, setRelatedClients] = useState([]);
    const [selectedClients, setSelectedClients] = usePersistedFilters('selectedClients', []);
    
    // Eisenhower Matrix filters - persisted
    const [minImportanceFilter, setMinImportanceFilter] = usePersistedFilters('minImportance', 0);
    const [minUrgencyFilter, setMinUrgencyFilter] = usePersistedFilters('minUrgency', 0);
    
    // Deadline filter - can be set from dashboard card for overdue items
    const [deadlineBeforeFilter, setDeadlineBeforeFilter] = usePersistedFilters('deadlineBeforeFilter', '');
    
    // Action item status filter
    const [actionItemStatusFilter, setActionItemStatusFilter] = usePersistedFilters('actionItemStatusFilter', '');
    
    // Today's date for deadline filter options
    const today = useMemo(() => new Date().toISOString().split('T')[0], []);

    // Eisenhower Matrix visibility - persisted to localStorage (shared across views)
    const [showEisenhowerMatrix, setShowEisenhowerMatrix] = useState(() => {
        const saved = localStorage.getItem(EISENHOWER_VISIBLE_KEY);
        return saved === 'true';
    });
    
    const toggleEisenhowerMatrix = () => {
        setShowEisenhowerMatrix(prev => {
            const newState = !prev;
            localStorage.setItem(EISENHOWER_VISIBLE_KEY, String(newState));
            return newState;
        });
    };

    const hasActiveFilters = selectedDris.length > 0 || actionItemAssigneeFilter !== '' || actionItemInitiativeFilter !== '' || minImportanceFilter > 0 || minUrgencyFilter > 0 || deadlineBeforeFilter !== '' || selectedClients.length > 0 || actionItemStatusFilter !== '';
    
    const handleClearAllFilters = () => {
        setSelectedDris([]);
        setActionItemAssigneeFilter('');
        setActionItemInitiativeFilter('');
        setMinImportanceFilter(0);
        setMinUrgencyFilter(0);
        setDeadlineBeforeFilter('');
        setSelectedClients([]);
        setActionItemStatusFilter('');
    };

    const loadDris = useCallback(async () => {
        if (!apiService || !selectedDepartment) return;
        try {
            const driResult = await apiService.getDepartmentDRIs(selectedDepartment);
            const driNames = driResult.map(r => r.dri);
            setDris(driNames);
            setStaffList(driNames); // Use department DRIs as staff list for assignments
        } catch (err) {
            console.error('Error loading DRIs:', err);
        }
    }, [apiService, selectedDepartment]);

    const loadRelatedClients = useCallback(async () => {
        if (!apiService || !selectedDepartment || !dateRange || !dateRange.start || !dateRange.end) return;
        try {
            const clientResult = await apiService.getDepartmentRelatedClients(selectedDepartment, dateRange.start, dateRange.end);
            setRelatedClients(clientResult.map(r => r.clientName).filter(Boolean));
        } catch (err) {
            console.error('Error loading related clients:', err);
        }
    }, [apiService, selectedDepartment, dateRange]);

    useEffect(() => {
        if (selectedDepartment) {
            loadDris();
        }
    }, [selectedDepartment, loadDris]);

    useEffect(() => {
        if (selectedDepartment && dateRange && dateRange.start && dateRange.end) {
            loadRelatedClients();
        }
    }, [selectedDepartment, dateRange, loadRelatedClients]);

    // Load all clients for create modal
    useEffect(() => {
        const loadAllClients = async () => {
            if (!apiService) return;
            try {
                const clients = await apiService.getClients();
                setAllClients(clients);
            } catch (err) {
                console.error('Error loading clients:', err);
            }
        };
        loadAllClients();
    }, [apiService]);

    const handleDriChange = (dri) => {
        setSelectedDris(prev =>
            prev.includes(dri) ? prev.filter(d => d !== dri) : [...prev, dri]
        );
    };

    const handleClientChange = (client) => {
        setSelectedClients(prev =>
            prev.includes(client) ? prev.filter(c => c !== client) : [...prev, client]
        );
    };

    // Filter action items by deadline, importance, urgency, and status
    const filteredActionItems = useMemo(() => {
        let filtered = actionItemsList.filter(item =>
            (item.importanceScore || 0) >= minImportanceFilter &&
            (item.urgencyScore || 0) >= minUrgencyFilter
        );
        if (deadlineBeforeFilter) {
            filtered = filtered.filter(item => {
                if (!item.deadline) return false;
                return item.deadline < deadlineBeforeFilter;
            });
        }
        if (actionItemStatusFilter) {
            filtered = filtered.filter(item => item.status === actionItemStatusFilter);
        }
        return filtered;
    }, [actionItemsList, deadlineBeforeFilter, minImportanceFilter, minUrgencyFilter, actionItemStatusFilter]);

    const loadFilterOptions = useCallback(async () => {
        if (!apiService || !departmentIdFromContext) return;
        setLoadingAssignees(true); setLoadingInitiativesForFilter(true);
        setAssigneeList([]); setInitiativeListForFilter([]);
        try {
            // For now, we'll use the department action items endpoint to get assignees and initiatives
            // In a full implementation, you might want to add specific endpoints for these
            const actionItems = await apiService.getDepartmentActionItems(departmentIdFromContext, {
                startDate: dateRange?.start || new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                endDate: dateRange?.end || new Date().toISOString().split('T')[0]
            });
            
            // Extract unique assignees and initiatives from the action items
            const assignees = [...new Set(actionItems.map(item => item.assigned_to).filter(Boolean))];
            const initiatives = [...new Set(actionItems.map(item => item.initiativeNames).flat().filter(Boolean))];
            
            setAssigneeList(assignees);
            setInitiativeListForFilter(initiatives);
        } catch (err) { console.error("Error loading filter options:", err); }
        finally { setLoadingAssignees(false); setLoadingInitiativesForFilter(false); }
    }, [apiService, departmentIdFromContext, dateRange]);

    // Load departments
    const loadDepartments = useCallback(async () => {
        if (!apiService) return;
        setLoadingDepartments(true);
        try {
            const deptResult = await apiService.getDepartmentList();
            const deptList = deptResult.map(r => r.department).filter(d => d);
            setDepartments(deptList);
            
            if (!selectedDepartment && activeRouteDepartmentId && deptList.includes(activeRouteDepartmentId)) {
                setSelectedDepartment(activeRouteDepartmentId);
            }
        } catch (err) {
            console.error('Error loading departments:', err);
        } finally {
            setLoadingDepartments(false);
        }
    }, [apiService, selectedDepartment, activeRouteDepartmentId]);

    useEffect(() => {
        loadDepartments();
    }, [loadDepartments]);

    useEffect(() => {
        if (departmentIdFromContext) {
            loadFilterOptions();
        }
    }, [loadFilterOptions, departmentIdFromContext])

    useEffect(() => {
        if (activeRouteDepartmentId && activeRouteDepartmentId !== selectedDepartment) {
            setSelectedDepartment(activeRouteDepartmentId);
        }
    }, [activeRouteDepartmentId, selectedDepartment]);


    const handleDepartmentChange = (newDept) => {
        navigate(`${ROUTES.OPERATIONS}/${newDept}/action-items`);
    };

    const loadDepartmentActionItems = useCallback(async () => {
        if (!apiService || !selectedDepartment || !dateRange || !dateRange.start || !dateRange.end) return;
        setLoading(true); setError(null);
        try {
            const items = await apiService.getDepartmentActionItemsList(
                selectedDepartment,
                dateRange.start,
                dateRange.end,
                selectedDris.length > 0 ? selectedDris : null,
                actionItemInitiativeFilter || null,
                selectedClients.length > 0 ? selectedClients : null,
                actionItemAssigneeFilter || null
            ); 
            setActionItemsList(items);
        } catch (err) { setError(err.message); setActionItemsList([]); }
        finally { setLoading(false); }
    }, [apiService, selectedDepartment, dateRange, actionItemInitiativeFilter, selectedDris, selectedClients, actionItemAssigneeFilter]);

    useEffect(() => {
        if (selectedDepartment) {
            loadDepartmentActionItems();
        }
    }, [selectedDepartment, loadDepartmentActionItems]);

    // Handle action item status update
    const handleActionItemStatusChange = useCallback(async (actionItemId, newStatus) => {
        if (!apiService) return;
        try {
            await apiService.updateNodeStatus(actionItemId, newStatus);
            setActionItemsList(prevList =>
                prevList.map(item =>
                    item.id === actionItemId ? { ...item, status: newStatus } : item
                )
            );
        } catch (err) {
            console.error('Error updating action item status:', err);
        }
    }, [apiService]);

    // Handle action item deadline update
    const handleActionItemDeadlineChange = useCallback(async (actionItemId, newDeadline) => {
        if (!apiService) return;
        try {
            await apiService.updateNodeProperty(actionItemId, 'deadline', newDeadline);
            setActionItemsList(prevList =>
                prevList.map(item =>
                    item.id === actionItemId ? { ...item, deadline: newDeadline } : item
                )
            );
        } catch (err) {
            console.error('Error updating action item deadline:', err);
        }
    }, [apiService]);

    // Handle action item assignee update
    const handleActionItemAssigneeChange = useCallback(async (actionItemId, newAssignee) => {
        if (!apiService) return;
        try {
            await apiService.updateNodeProperty(actionItemId, 'assigned_to', newAssignee);
            setActionItemsList(prevList =>
                prevList.map(item =>
                    item.id === actionItemId ? { ...item, assigned_to: newAssignee, assignee: newAssignee } : item
                )
            );
        } catch (err) {
            console.error('Error updating action item assignee:', err);
        }
    }, [apiService]);

    // Handle action item creation
    const handleActionItemCreated = useCallback((newItem) => {
        if (!newItem) {
            loadDepartmentActionItems();
            return;
        }
        // Add the new item to the list with the expected fields
        const formattedItem = {
            ...newItem,
            assigned_to: newItem.assignee || newItem.assigned_to,
            assignee: newItem.assignee || newItem.assigned_to,
            initiativeNames: [],
            score: 0,
            importanceScore: 0,
            urgencyScore: 1.5, // Default for open items
        };
        setActionItemsList(prev => [formattedItem, ...prev]);
    }, [loadDepartmentActionItems]);

    // Custom API service wrapper for creating department action items
    // Uses a Proxy to forward all methods to apiService, but overrides createActionItem
    const departmentApiService = useMemo(() => {
        if (!apiService) return null;
        return new Proxy(apiService, {
            get(target, prop) {
                if (prop === 'createActionItem') {
                    // Route to department endpoint instead of client endpoint
                    return (clientId, data) => target.createDepartmentActionItem(selectedDepartment, data);
                }
                // Forward all other methods to the original apiService
                const value = target[prop];
                if (typeof value === 'function') {
                    return value.bind(target);
                }
                return value;
            }
        });
    }, [apiService, selectedDepartment]);

    return (
        <>
            {/* SmartFilterBar for entity navigation */}
            <SmartFilterBar
                dateRange={dateRange} onDateRangeChange={setDateRange}
                dris={dris} selectedDris={selectedDris} onDriChange={handleDriChange} driDisplayFormatter={getDriDisplayName}
                filterGroups={[
                    { id: 'context', label: 'Context', filters: [{ key: 'department' }, { key: 'client' }] },
                    { id: 'actions', label: 'Action Items', filters: [{ key: 'deadline' }, { key: 'initiative' }, { key: 'assignee' }, { key: 'actionStatus' }] }
                ]}
                filters={{ department: selectedDepartment, client: selectedClients, deadline: deadlineBeforeFilter, initiative: actionItemInitiativeFilter, assignee: actionItemAssigneeFilter, actionStatus: actionItemStatusFilter }}
                filterConfigs={{
                    department: { label: 'Department', render: () => departments.length > 0 && <DepartmentSelector departments={departments} selectedDepartment={selectedDepartment} onDepartmentChange={handleDepartmentChange} loading={loadingDepartments} /> },
                    client: { label: 'Client', render: () => <DropdownCheckbox options={relatedClients} selectedOptions={selectedClients} onChange={handleClientChange} title="Client" /> },
                    deadline: { label: 'Deadline', render: () => <SingleSelectDropdown id="deadlineFilter" label="Deadline" value={deadlineBeforeFilter === today ? 'overdue' : (deadlineBeforeFilter || '')} onChange={(val) => setDeadlineBeforeFilter(val === 'overdue' ? today : '')} options={['overdue']} allLabel="All" capitalize={true} /> },
                    initiative: { label: 'Initiative', render: () => <SingleSelectDropdown id="initiativeFilter" label="Initiative" value={actionItemInitiativeFilter} onChange={(val) => setActionItemInitiativeFilter(val)} options={initiativeListForFilter} allLabel="All" loading={loadingInitiativesForFilter} capitalize={false} /> },
                    assignee: { label: 'Assignee', render: () => <SingleSelectDropdown id="assigneeFilter" label="Assignee" value={actionItemAssigneeFilter} onChange={(val) => setActionItemAssigneeFilter(val)} options={assigneeList} allLabel="All" loading={loadingAssignees} capitalize={false} /> },
                    actionStatus: { label: 'Status', render: () => <SingleSelectDropdown id="actionStatusFilter" label="Status" value={actionItemStatusFilter} onChange={(val) => setActionItemStatusFilter(val)} options={ACTION_ITEM_STATUS_OPTIONS} allLabel="All" capitalize={true} /> }
                }}
                activeFilterChips={[
                    ...(selectedClients.length > 0 ? [{ label: 'Clients', value: selectedClients.length === 1 ? selectedClients[0] : `${selectedClients.length} selected`, onRemove: () => setSelectedClients([]) }] : []),
                    ...(deadlineBeforeFilter ? [{ label: 'Deadline', value: 'Overdue', onRemove: () => setDeadlineBeforeFilter('') }] : []),
                    ...(actionItemInitiativeFilter ? [{ label: 'Initiative', value: actionItemInitiativeFilter, onRemove: () => setActionItemInitiativeFilter('') }] : []),
                    ...(actionItemAssigneeFilter ? [{ label: 'Assignee', value: actionItemAssigneeFilter, onRemove: () => setActionItemAssigneeFilter('') }] : []),
                    ...(actionItemStatusFilter ? [{ label: 'Status', value: actionItemStatusFilter.charAt(0).toUpperCase() + actionItemStatusFilter.slice(1), onRemove: () => setActionItemStatusFilter('') }] : []),
                ]}
                onClearAll={handleClearAllFilters} hasActiveFilters={hasActiveFilters}
                variant={filterVariant} isExpanded={filtersExpanded}
            />
            
            {/* Eisenhower Matrix Section - Collapsible */}
            <div className="mb-8 bg-white rounded-lg shadow overflow-hidden">
                <button
                    onClick={toggleEisenhowerMatrix}
                    className="w-full px-4 py-3 md:px-6 md:py-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors duration-200 border-b border-gray-200"
                >
                    <div className="flex items-center">
                        <svg 
                            className={`h-5 w-5 text-gray-500 mr-3 transition-transform duration-200 ${showEisenhowerMatrix ? 'rotate-90' : ''}`}
                            xmlns="http://www.w3.org/2000/svg" 
                            viewBox="0 0 20 20" 
                            fill="currentColor"
                        >
                            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                        </svg>
                        <div>
                            <h3 className="text-lg font-medium text-gray-900">Priority Matrix</h3>
                            <p className="text-xs text-gray-500 mt-0.5">Action Items prioritized by urgency and importance</p>
                        </div>
                    </div>
                </button>
                
                {showEisenhowerMatrix && (
                    <div className="p-4 md:p-6">
                        <EisenhowerMatrixView 
                            departmentId={selectedDepartment} 
                            clientId={null}
                            typeFilter={['ActionItem']}
                            driFilter={selectedDris}
                            clientFilter={selectedClients}
                            minImportanceFilter={minImportanceFilter}
                            minUrgencyFilter={minUrgencyFilter}
                            deadlineFilter={deadlineBeforeFilter === today ? 'overdue' : ''}
                            actionInitiativeFilter={actionItemInitiativeFilter}
                            assigneeFilter={actionItemAssigneeFilter}
                        />
                    </div>
                )}
            </div>

            {/* Action Items Card Grid */}
            <div className="rounded-xl shadow p-4 md:p-6" style={{ backgroundColor: 'var(--pallium-white)', boxShadow: 'var(--pallium-shadow-md)' }}>
                <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-1.5">
                        <h3 className="text-lg font-medium" style={{ color: 'var(--pallium-text-primary)' }}>
                            {deadlineBeforeFilter 
                                ? "Overdue Action Items" 
                                : actionItemStatusFilter 
                                    ? `${actionItemStatusFilter.charAt(0).toUpperCase() + actionItemStatusFilter.slice(1)} Action Items`
                                    : "Action Items"}
                        </h3>
                        <Tooltip content="Action items are shown if they are assigned to department staff, or if they have a related communication within the selected date range.">
                            <RiInformationLine className="w-4 h-4 cursor-help" style={{ color: 'var(--pallium-icon-gray)' }} />
                        </Tooltip>
                    </div>
                    <button
                        onClick={() => setShowCreateModal(true)}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg text-white transition-colors"
                        style={{ backgroundColor: 'var(--pallium-success-green)' }}
                        disabled={!selectedDepartment}
                    >
                        <RiAddLine className="w-4 h-4" />
                        New Action Item
                    </button>
                </div>
                <p className="text-xs mb-4" style={{ color: 'var(--pallium-text-secondary)' }}>
                    Action items related to {selectedDepartment || 'this department'}
                </p>
                
                {loading ? (
                    <div className="text-center text-sm py-8" style={{ color: 'var(--pallium-icon-gray)' }}>Loading action items...</div>
                ) : error ? (
                    <div className="text-center text-sm py-8" style={{ color: 'var(--pallium-error-red)' }}>Error: {error}</div>
                ) : filteredActionItems.length === 0 ? (
                    <div className="text-center text-sm py-8" style={{ color: 'var(--pallium-icon-gray)' }}>
                        No {deadlineBeforeFilter ? 'overdue' : actionItemStatusFilter ? actionItemStatusFilter : ''} action items found matching the current filters.
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {filteredActionItems.map((actionItem, index) => (
                            <ActionItemCard
                                key={actionItem.id || index}
                                actionItem={{
                                    ...actionItem,
                                    assignee: actionItem.assigned_to,
                                    initiativeName: Array.isArray(actionItem.initiativeNames) ? actionItem.initiativeNames.join(', ') : actionItem.initiativeNames
                                }}
                                clientId={null}
                                onClick={() => navigate(`${ROUTES.OPERATIONS}/${selectedDepartment}/action-items/${actionItem.id}`)}
                                editable={true}
                                statusOptions={ACTION_ITEM_STATUS_OPTIONS}
                                assigneeOptions={assigneeList}
                                onStatusChange={handleActionItemStatusChange}
                                onDeadlineChange={handleActionItemDeadlineChange}
                                onAssigneeChange={handleActionItemAssigneeChange}
                                data-testid="action-card"
                            />
                        ))}
                    </div>
                )}
            </div>

            {/* Create Action Item Modal - Single step with optional account selection */}
            {showCreateModal && (
                <CreateEntityModal
                    isOpen={true}
                    onClose={() => setShowCreateModal(false)}
                    entityType="action-item"
                    clientId={null}
                    apiService={apiService}
                    onSuccess={handleActionItemCreated}
                    staffList={staffList}
                    initiativesList={[]}
                    clientsList={allClients}
                    departmentId={selectedDepartment}
                    departmentApiService={departmentApiService}
                />
            )}
        </>
    );
};

export default InternalActionItemListView;